import { useCallback, useEffect, useState } from 'react'

/** Content size of an element, updated on resize. */
export function useSize<T extends HTMLElement>(): [(node: T | null) => void, { w: number; h: number }] {
  const [node, setNode] = useState<T | null>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  const ref = useCallback((n: T | null) => setNode(n), [])
  useEffect(() => {
    if (!node) return
    const read = () => setSize(s => (s.w === node.clientWidth && s.h === node.clientHeight ? s : { w: node.clientWidth, h: node.clientHeight }))
    read()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(read)
    ro.observe(node)
    return () => ro.disconnect()
  }, [node])
  return [ref, size]
}

/** True under the phone breakpoint of the shell (≤599 px). */
export function usePhone(): boolean {
  const query = '(max-width: 599px)'
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(query).matches)
  useEffect(() => {
    const m = window.matchMedia?.(query)
    if (!m) return
    const on = () => setPhone(m.matches)
    m.addEventListener('change', on)
    return () => m.removeEventListener('change', on)
  }, [])
  return Boolean(phone)
}

/** True on wide screens (≥1200 px), where the stage has room between the fighters. */
export function useWide(): boolean {
  const query = '(min-width: 1200px)'
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && Boolean(window.matchMedia?.(query).matches))
  useEffect(() => {
    const m = window.matchMedia?.(query)
    if (!m) return
    const on = () => setWide(m.matches)
    m.addEventListener('change', on)
    return () => m.removeEventListener('change', on)
  }, [])
  return wide
}
